import { mergePendingParams, splitRouteParams } from '@aline/common';
import { stickyRouteParamKeys } from '@aline/routes';

import { getRoutesMatchSameResource } from './tab-route-matching';
import type { Tab } from './tab-types';

// Routes are paths; parsing one needs a base, and none of it leaves this module.
const ROUTE_BASE = 'http://tab.local';

/**
 * Applies an incoming route to a tab: durable search stays on `route`, ephemeral
 * keys merge into `pendingParams` without clobbering unconsumed keys.
 */
export function applyIncomingRoute(
  tab: Pick<Tab, 'pendingParams'>,
  incomingRoute: string
): Pick<Tab, 'pendingParams' | 'route'> {
  const { durableRoute, pendingParams } = splitRouteParams(incomingRoute);
  return {
    pendingParams: mergePendingParams(tab.pendingParams, pendingParams),
    route: durableRoute,
  };
}

/**
 * Carries the sticky params (`?taskId` on a document opened for review) from a tab's route onto
 * the next view of the same resource, unless that route sets them itself. Leaving the resource
 * drops them.
 */
export function getRouteWithStickyParams(fromRoute: string | undefined, toRoute: string): string {
  if (!fromRoute || !getRoutesMatchSameResource(fromRoute, toRoute)) return toRoute;

  const fromParams = new URL(fromRoute, ROUTE_BASE).searchParams;
  const next = new URL(toRoute, ROUTE_BASE);
  const carried = stickyRouteParamKeys.filter(
    key => fromParams.has(key) && !next.searchParams.has(key)
  );
  if (carried.length === 0) return toRoute;

  carried.forEach(key => next.searchParams.set(key, fromParams.get(key) ?? ''));
  return `${next.pathname}${next.search}${next.hash}`;
}
